/**
 * Log Service Record Component
 * ============================
 * Form for logging a new service record for a fleet vehicle.
 * Includes AI cost estimate integration.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Loader2,
  Wrench,
  Calendar,
  DollarSign,
  Gauge,
  Building,
  FileText,
  User,
  Sparkles,
  X,
  Check,
  AlertCircle,
} from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fleetApi } from '../../services/fleetApi';

// Service type options
const SERVICE_TYPES = [
  { value: 'preventive', label: 'Preventive Maintenance', description: 'Scheduled maintenance like oil changes, tire rotations' },
  { value: 'repair', label: 'Repair', description: 'Fixing broken or worn components' },
  { value: 'inspection', label: 'Inspection', description: 'Safety inspections, emissions tests' },
  { value: 'recall', label: 'Recall', description: 'Manufacturer recall service' },
  { value: 'other', label: 'Other', description: 'Other service types' },
];

// AI Cost Estimate Modal
const CostEstimateModal = ({ isOpen, onClose, vehicle, serviceType, description, onApply }) => {
  const [estimate, setEstimate] = useState(null);
  
  const estimateMutation = useMutation({
    mutationFn: () => fleetApi.getCostEstimate(
      vehicle?.vehicle_profile_id,
      serviceType,
      description
    ),
    onSuccess: (data) => setEstimate(data),
  });

  useEffect(() => {
    if (isOpen && description) {
      estimateMutation.mutate();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose} />
        
        <div className="relative bg-surface-card rounded-card border border-border-default shadow-overlay max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Sparkles className="w-5 h-5 text-info mr-2" />
              <h3 className="text-lg font-semibold">AI Cost Estimate</h3>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-surface-muted rounded">
              <X className="w-5 h-5 text-ink-muted" />
            </button>
          </div>

          {estimateMutation.isPending && (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 text-info animate-spin mx-auto mb-3" />
              <p className="text-ink-muted">Analyzing service costs...</p>
            </div>
          )}

          {estimateMutation.error && (
            <div className="py-8 text-center">
              <AlertCircle className="w-8 h-8 text-danger mx-auto mb-3" />
              <p className="text-danger">Could not generate estimate</p>
              <p className="text-sm text-ink-muted mt-1">{estimateMutation.error.message}</p>
            </div>
          )}

          {estimate && (
            <div>
              <div className="bg-info-tint rounded-lg p-4 mb-4">
                <p className="text-sm text-info mb-1">Estimated Total</p>
                <p className="text-3xl font-bold text-info">
                  ${estimate.total_estimate?.toFixed(2) || '0.00'}
                </p>
                <p className="text-small text-info mt-1">
                  Based on {vehicle?.vehicle_profile?.year} {vehicle?.vehicle_profile?.make} {vehicle?.vehicle_profile?.model}
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-ink-muted">Parts</span>
                  <span className="font-medium">${estimate.parts_estimate?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-muted">Labor</span>
                  <span className="font-medium">${estimate.labor_estimate?.toFixed(2) || '0.00'}</span>
                </div>
                {estimate.notes && (
                  <p className="text-sm text-ink-muted mt-3 p-3 bg-surface-page rounded">
                    {estimate.notes}
                  </p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-border-default rounded-lg text-ink-soft hover:bg-surface-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onApply({
                      cost_parts: estimate.parts_estimate,
                      cost_labor: estimate.labor_estimate,
                    });
                    onClose();
                  }}
                  className="flex-1 px-4 py-2 bg-info text-white rounded-lg hover:bg-info/80 flex items-center justify-center"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Apply Estimate
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
const LogServiceRecord = () => {
  const { fleetId, vehicleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prefillType = searchParams.get('type');

  const [showEstimate, setShowEstimate] = useState(false);
  const [formData, setFormData] = useState({
    service_type: 'preventive',
    description: prefillType || '',
    service_date: new Date().toISOString().split('T')[0],
    odometer_at_service: '',
    cost_parts: '',
    cost_labor: '',
    cost_tax: '',
    cost_other: '',
    vendor_name: '',
    vendor_address: '',
    vendor_phone: '',
    invoice_number: '',
    work_order_number: '',
    performed_by: '',
    approved_by: '',
    notes: '',
    update_odometer: true,
  });

  // Fetch vehicle details
  const { data: vehicle } = useQuery({
    queryKey: ['fleetVehicle', fleetId, vehicleId],
    queryFn: () => fleetApi.getVehicle(fleetId, vehicleId),
  });

  // Set default odometer from vehicle
  useEffect(() => {
    if (vehicle?.current_odometer && !formData.odometer_at_service) {
      setFormData(prev => ({
        ...prev,
        odometer_at_service: vehicle.current_odometer.toString(),
      }));
    }
  }, [vehicle]);

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: (data) => fleetApi.logService(fleetId, vehicleId, data),
    onSuccess: () => {
      navigate(`/fleet/${fleetId}/vehicles/${vehicleId}`, {
        state: { message: 'Service record logged successfully!' }
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      odometer_at_service: formData.odometer_at_service ? parseInt(formData.odometer_at_service) : null,
      cost_parts: formData.cost_parts ? parseFloat(formData.cost_parts) : 0,
      cost_labor: formData.cost_labor ? parseFloat(formData.cost_labor) : 0,
      cost_tax: formData.cost_tax ? parseFloat(formData.cost_tax) : 0,
      cost_other: formData.cost_other ? parseFloat(formData.cost_other) : 0,
    };

    submitMutation.mutate(submitData);
  };

  const handleApplyEstimate = (estimate) => {
    setFormData(prev => ({
      ...prev,
      cost_parts: estimate.cost_parts?.toFixed(2) || prev.cost_parts,
      cost_labor: estimate.cost_labor?.toFixed(2) || prev.cost_labor,
    }));
  };

  const calculateTotal = () => {
    return (
      parseFloat(formData.cost_parts || 0) +
      parseFloat(formData.cost_labor || 0) +
      parseFloat(formData.cost_tax || 0) +
      parseFloat(formData.cost_other || 0)
    ).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-surface-page">
      {/* Header */}
      <div className="bg-surface-card border-b border-border-default">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-ink-muted hover:text-ink-soft mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-ink-soft">Log Service Record</h1>
          {vehicle && (
            <p className="text-ink-muted mt-1">
              {vehicle.unit_number} - {vehicle.vehicle_profile?.display_name}
            </p>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          {/* Service Type */}
          <div className="bg-surface-card rounded-xl border border-border-default p-6 mb-6">
            <h2 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
              <Wrench className="w-5 h-5 mr-2 text-ink-muted" />
              Service Details
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">
                  Service Type *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {SERVICE_TYPES.map((type) => (
                    <label
                      key={type.value}
                      className={`relative flex items-center p-3 border rounded-lg cursor-pointer hover:bg-surface-muted ${
                        formData.service_type === type.value
                          ? 'border-accent bg-accent/10'
                          : 'border-border-default'
                      }`}
                    >
                      <input
                        type="radio"
                        name="service_type"
                        value={type.value}
                        checked={formData.service_type === type.value}
                        onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                        className="sr-only"
                      />
                      <span className={`text-sm font-medium ${
                        formData.service_type === type.value ? 'text-accent' : 'text-ink-soft'
                      }`}>
                        {type.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Describe the service performed..."
                  required
                  className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Service Date *
                  </label>
                  <input
                    type="date"
                    value={formData.service_date}
                    onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-2">
                    <Gauge className="w-4 h-4 inline mr-1" />
                    Odometer
                  </label>
                  <input
                    type="number"
                    value={formData.odometer_at_service}
                    onChange={(e) => setFormData({ ...formData, odometer_at_service: e.target.value })}
                    placeholder="Current reading"
                    min="0"
                    className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Costs */}
          <div className="bg-surface-card rounded-xl border border-border-default p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-ink-soft flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-ink-muted" />
                Costs
              </h2>
              <button
                type="button"
                onClick={() => setShowEstimate(true)}
                disabled={!formData.description}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-info bg-info-tint rounded-lg hover:bg-info-tint/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Get AI Estimate
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">Parts</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">$</span>
                  <input
                    type="number"
                    value={formData.cost_parts}
                    onChange={(e) => setFormData({ ...formData, cost_parts: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">Labor</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">$</span>
                  <input
                    type="number"
                    value={formData.cost_labor}
                    onChange={(e) => setFormData({ ...formData, cost_labor: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">Tax</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">$</span>
                  <input
                    type="number"
                    value={formData.cost_tax}
                    onChange={(e) => setFormData({ ...formData, cost_tax: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">Other</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">$</span>
                  <input
                    type="number"
                    value={formData.cost_other}
                    onChange={(e) => setFormData({ ...formData, cost_other: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-border-default flex justify-between items-center">
              <span className="text-ink-muted font-medium">Total</span>
              <span className="text-2xl font-bold text-ink-soft">${calculateTotal()}</span>
            </div>
          </div>

          {/* Vendor */}
          <div className="bg-surface-card rounded-xl border border-border-default p-6 mb-6">
            <h2 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
              <Building className="w-5 h-5 mr-2 text-ink-muted" />
              Vendor Information
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-ink-soft mb-2">Vendor Name</label>
                <input
                  type="text"
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                  placeholder="Shop or service provider name"
                  className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">Invoice #</label>
                <input
                  type="text"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="Invoice number"
                  className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">Work Order #</label>
                <input
                  type="text"
                  value={formData.work_order_number}
                  onChange={(e) => setFormData({ ...formData, work_order_number: e.target.value })}
                  placeholder="Work order number"
                  className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">Performed By</label>
                <input
                  type="text"
                  value={formData.performed_by}
                  onChange={(e) => setFormData({ ...formData, performed_by: e.target.value })}
                  placeholder="Technician name"
                  className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">Approved By</label>
                <input
                  type="text"
                  value={formData.approved_by}
                  onChange={(e) => setFormData({ ...formData, approved_by: e.target.value })}
                  placeholder="Approver name"
                  className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-surface-card rounded-xl border border-border-default p-6 mb-6">
            <h2 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-ink-muted" />
              Additional Notes
            </h2>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Any additional notes or observations..."
              className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
            />

            <label className="flex items-center mt-4">
              <input
                type="checkbox"
                checked={formData.update_odometer}
                onChange={(e) => setFormData({ ...formData, update_odometer: e.target.checked })}
                className="w-4 h-4 text-accent border-border-default rounded focus:ring-accent"
              />
              <span className="ml-2 text-sm text-ink-muted">
                Update vehicle's current odometer with this reading
              </span>
            </label>
          </div>

          {/* Error */}
          {submitMutation.error && (
            <div className="mb-6 p-4 bg-danger-bg border border-danger rounded-lg flex items-center text-danger">
              <AlertCircle className="w-5 h-5 mr-2" />
              {submitMutation.error.message}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-3 border border-border-default rounded-lg text-ink-soft font-medium hover:bg-surface-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitMutation.isPending}
              className="px-6 py-3 bg-accent text-white rounded-lg font-medium hover:bg-accent-hover disabled:opacity-50 flex items-center"
            >
              {submitMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Service Record
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Cost Estimate Modal */}
      <CostEstimateModal
        isOpen={showEstimate}
        onClose={() => setShowEstimate(false)}
        vehicle={vehicle}
        serviceType={formData.service_type}
        description={formData.description}
        onApply={handleApplyEstimate}
      />
    </div>
  );
};

export default LogServiceRecord;
